import React, { useEffect, useState } from 'react';
import useRouting from '../hooks/useRouting';
import { useSearchParams } from 'react-router-dom';
import { FilledButton } from '../components/common/Button';
import { EUserType } from '../data_models/enums';

import useCurrentUser from '../hooks/useCurrentUser';
import { TbUserDollar, TbUserEdit } from 'react-icons/tb';
import { BRAND_ASSETS } from '../config/defaultAssetUrls';

import useAuth from '../hooks/useAuth';
import useGlobalLoading from '../hooks/useGlobalLoading';

import TextInputField from '../components/common/TextInputField';
import { FORGOT_PASSWORD_ROUTE, SIGNUP_ROUTE } from '../App';
import { isEmail } from '../utils/regularExpressions';

const LoginPage: React.FC = () => {

  const [searchParams] = useSearchParams();
  const type = (searchParams.get('type') as EUserType) || EUserType.FREELANCER;
  const [userType, setUserType] = useState<EUserType>(type);

  const { isLoggedIn } = useCurrentUser();
  const { loginFreelancer, loginEmployer, authToken } = useAuth();
  const { goToDashboard } = useRouting();

  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [errorServerFeedback, setErrorServerFeedback] = useState<string>('');

  const onChangeUserType = (userType: EUserType) => {
    const url = new URL(window.location.href);
    url.searchParams.set('type', userType);
    window.history.replaceState({}, '', url);
    setUserType(userType);
  }

  const validateFields = () => {
    let valid = true;

    // check email
    if (!email) {
      setEmailError('Email is required');
      valid = false;
    } else {
      if (!isEmail(email)) {
        setEmailError('Invalid email address');
        valid = false;
      }
    }

    // check password
    if (!password) {
      setPasswordError('Password is required');
      valid = false;
    }
    return valid;
  }

  const resetErrors = () => {
    setEmailError('');
    setPasswordError('');
  }



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const valid = validateFields();
    if (!valid) {
      return;
    }

    resetErrors();
    setLoading(true);

    if (userType === EUserType.EMPLOYER) {
      await loginEmployer(email, password)
        .catch((e: any) => {
          setErrorServerFeedback(e.message);
        }).finally(() => {
          setLoading(false);

        });
    } else {
      await loginFreelancer(email, password)
        .catch((e: any) => {
          setErrorServerFeedback(e.message);
        }).finally(() => {
          setLoading(false);
        });
    }
  }


  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Blurred dots */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-primary-500/30 blur-3xl" />
      <div className="absolute -top-12 left-64 w-64 h-64 rounded-full bg-primary-700/25 blur-3xl" />
      <div className="absolute -bottom-10 -right-5 w-64 h-64 rounded-full bg-primary-500/20 blur-3xl" />
      <div className="absolute bottom-20 right-5 w-64 h-64 rounded-full bg-secondary-500/20 blur-3xl" />
      <div className="absolute bottom-12 left-12 w-48 h-48 rounded-full bg-primary-200/30 blur-3xl" />

      {/* Content container */}
      <div className="relative z-10 flex flex-col min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-5rem)] max-w-7xl mx-auto">
        <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <img
              alt="Your Company"
              src={BRAND_ASSETS.LOGO_SMALL_BLACK}
              className="mx-auto h-10 w-auto"
            />
            <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
              Log in to your account
            </h2>
          </div>

          {/* Login Form */}
          <div className="flex flex-col -space-y-6 md:space-y-0 md:flex-row md:justify-center mt-10 mx-auto w-full md:max-w-[650px]">
            {/* User Type - Mobile */}
            <div className='z-10 md:hidden text-sm/6 grid grid-cols-2 gap-4 px-4'>
              <p className={` p-4 text-center font-bold cursor-pointer rounded-lg border border-b-2 transition-all duration-200
                ${userType === EUserType.FREELANCER
                  ? 'bg-primary-50 border-primary/30 border-b-primary shadow-md text-primary-600'
                  : 'bg-white text-gray-500 border-gray-400/30 border-b-gray-400'}`}
                onClick={() => onChangeUserType(EUserType.FREELANCER)}>
                I am a Freelancer</p>
              <p className={`p-4 text-center font-bold cursor-pointer rounded-lg border border-b-2 transition-all duration-200
                ${userType === EUserType.EMPLOYER
                  ? 'bg-primary-50 border-primary/30 border-b-primary shadow-md text-primary-600'
                  : 'bg-white text-gray-500 border-gray-400/30 border-b-gray-400'}`}
                onClick={() => onChangeUserType(EUserType.EMPLOYER)}>
                I have Projects</p>
            </div>

            {/* User Type - Desktop */}
            <div className='hidden -mr-10 md:grid z-10 text-sm/6 grid-cols-1 gap-4 px-4 pt-10 auto-rows-min'>
              <p className={`p-4 text-center font-bold cursor-pointer md:rounded-lg border border-r-2 transition-all duration-200
                ${userType === EUserType.FREELANCER
                  ? 'bg-primary-50 border-primary/30 border-r-primary shadow-md text-primary-600'
                  : 'bg-white text-gray-500 border-gray-400/30 border-r-gray-400'}`}
                onClick={() => onChangeUserType(EUserType.FREELANCER)}>
                I am a Freelancer</p>
              <p className={`p-4 text-center font-bold cursor-pointer md:rounded-lg border border-r-2 transition-all duration-200
                ${userType === EUserType.EMPLOYER
                  ? 'bg-primary-50 border-primary/30 border-r-primary shadow-md text-primary-600'
                  : 'bg-white text-gray-500 border-gray-400/30 border-r-gray-400'}`}
                onClick={() => onChangeUserType(EUserType.EMPLOYER)}>
                I have Projects</p>
            </div>

            {/* Form */}
            <div className="md:-mt-5 bg-white px-6 py-12 shadow-md border border-gray-100 md:rounded-lg md:px-12 flex-1">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>

                  <div className="mt-2">
                    <TextInputField
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      required
                      autoComplete="email"
                      label="Email address"
                      error={emailError}
                    />

                  </div>
                </div>

                <div>
                  <TextInputField
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                    required
                    autoComplete="current-password"
                    label="Password"
                    error={passwordError}
                  />
                </div>


                <div className="flex items-center justify-end">


                  <div className="text-sm/6">
                    <a href={`${FORGOT_PASSWORD_ROUTE}`} className="font-semibold text-primary-600 hover:text-primary-500">
                      Forgot password?
                    </a>
                  </div>
                </div>


                <div>
                  <FilledButton
                    fullWidth
                    type='submit'
                    isLoading={loading}
                  >Login
                  </FilledButton>

                  {errorServerFeedback && <p className='mt-1 text-red-500 text-center text-sm/6'>{errorServerFeedback}</p>}
                </div>
              </form>

            </div>

          </div>
          <p className="mt-10 text-center text-sm/6 text-gray-500">
            Do you not yet have an account?{' '}
            <a href={`${SIGNUP_ROUTE}?type=${userType}`} className="font-semibold text-primary-600 hover:text-primary-500">
              Sign up
            </a>
          </p>
        </div>

      </div>
    </div>
  );
};

export default LoginPage;
